import { Outlet, RouterProvider, useNavigate } from 'react-router';
import { type ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
// RUM のため react-router からではなく @datadog/browser-rum-react/react-router-v7 から import する
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v7';
import { layout } from '../styled-system/recipes';
import {
  integrationSignInCallbackLoader,
  IntegrationSignInCallbackPage,
} from './pages/Integration/IntegrationSignInCallback';
import { GlobalHeader } from '#/components/GlobalHeader';
import { GlobalFooter } from '#/components/GlobalFooter';
import { MyPage } from '#/pages/MyPage';
import { EmailChangePage } from '#/pages/EmailChangePage';
// https://github.com/dw-zenid/zenid/issues/406
// 暫定対応のためZEN ID側のプライバシーポリシー画面はコメントアウト
// import { PrivacyPolicy } from '#/pages/PrivacyPolicy';
import { Terms } from '#/pages/Terms';
import { NoMatch } from '#/pages/NoMatch/NoMatch';
import { GenericError } from '#/components/Error/GenericError';
import { Login } from '#/pages/Login';
import { IntegrationFormPage, IntegrationVerifyPage, integrationVerifyLoader } from '#/pages/Integration';
import { CardLayout } from '#/components/Layout/CardLayout';
import { AuthProvider } from '#/utils/auth/AuthProvider';
import { auth0Client } from '#/utils/auth/auth0Client';

const FEATURE_FLAG_NNN_SSO = Boolean(import.meta.env.VITE_FEATURE_FLAG_NNN_SSO);

function AppLayout({ children }: { children: ReactNode }) {
  const { zenidContainer, mainContentsContainer, mainContentsInnerContainer, errorContainer } = layout();
  return (
    <div
      className={zenidContainer}
      style={{ backgroundImage: `url(${import.meta.env.VITE_ASSETS_URL}/assets/images/zenid_bg.png)` }}
    >
      <GlobalHeader />
      <div className={mainContentsContainer}>
        <div className={mainContentsInnerContainer}>
          <ErrorBoundary
            fallback={
              <div className={errorContainer}>
                <GenericError />
              </div>
            }
          >
            {children}
          </ErrorBoundary>
        </div>
      </div>
      <GlobalFooter />
    </div>
  );
}

// 外部 URL への open redirect を防ぐため、ログイン後の復帰先はアプリ内パスに限定する
function internalReturnTo(returnTo: string | undefined): string | undefined {
  return returnTo?.startsWith('/') && !returnTo.startsWith('//') ? returnTo : undefined;
}

function Provider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <AuthProvider
      client={auth0Client}
      onRedirectCallback={(appState) => navigate(internalReturnTo(appState?.returnTo) ?? window.location.pathname)}
    >
      {children}
    </AuthProvider>
  );
}

/**
 * アプリ全体のルーティング情報を管理する
 *
 * loader / action などのデータ API を利用するため Data モード（createBrowserRouter + RouterProvider）で構成する
 */
const router = createBrowserRouter([
  FEATURE_FLAG_NNN_SSO
    ? {
        path: 'integration/callback',
        element: (
          <AppLayout>
            <CardLayout>
              <IntegrationSignInCallbackPage />
            </CardLayout>
          </AppLayout>
        ),
        loader: integrationSignInCallbackLoader,
        hydrateFallbackElement: <div />, // 処理中にも AppLayout は表示されるようにする。処理が長いようであれば loading を検討
      }
    : {},
  {
    path: '/',
    element: (
      <Provider>
        <AppLayout>
          <Outlet />
        </AppLayout>
      </Provider>
    ),
    children: [
      { path: 'login', element: <Login /> },
      { path: 'home', element: <MyPage /> },
      {
        /**
         * Card レイアウトのページ郡
         */
        element: (
          <CardLayout>
            <Outlet />
          </CardLayout>
        ),
        children: [
          { path: 'edit/email', element: <EmailChangePage /> },
          FEATURE_FLAG_NNN_SSO ? { path: 'integration/form', element: <IntegrationFormPage /> } : {},
          FEATURE_FLAG_NNN_SSO
            ? {
                path: 'integration/verify',
                element: <IntegrationVerifyPage />,
                loader: integrationVerifyLoader,
                hydrateFallbackElement: <div />, // 処理中にも AppLayout は表示されるようにする。処理が長いようであれば loading を検討
              }
            : {},
        ],
      },
      {
        path: 'terms',
        // https://github.com/dw-zenid/zenid/issues/406
        // 暫定対応のためZEN ID側のプライバシーポリシー画面はコメントアウト
        // { path: 'privacy', element: <PrivacyPolicy /> },
        children: [{ index: true, element: <Terms /> }],
      },
      { path: '*', element: <NoMatch /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
